/**
 * GJESTEMODUS (sak 232, T5; PLAN §1.7 og risiko 5).
 *
 * En ekstern åpner det delte I ORGANISASJONEN SOM DELTE, som gjest. Serveren velger en
 * gjesteorganisasjon bare når `X-Tvitvi-Organization` navngir den (`TenantGuard`), så nettleseren må
 * sende den — men organisasjonsheaderen er global, og en fane som sendte den til alt ville tatt med
 * seg gjestemodus inn i neste fane og neste innlogging.
 *
 * Derfor `sessionStorage`, PER FANE: en omlasting blir stående i gjestemodus, en ny fane starter i
 * personens egne organisasjoner, og å logge ut glemmer den. Den skriver ALDRI `last_active` — ingen
 * kall herfra, og `PUT /v1/me/active-organization` nekter en gjesteorganisasjon uansett.
 *
 * Gjestens delinger i organisasjonen (`guestScope`) ligger her også, fordi `actorFromMe` er synkron og
 * leses av menyen, rutevaktene og knappene: den samme `can()` som API-et, med de samme delingene.
 */
import type { GuestScope } from '@tvitvi/shared';
import { useSyncExternalStore } from 'react';

const ORGANIZATION_KEY = 'tvitvi.guestOrganizationId';
const RETURN_KEY = 'tvitvi.guestReturnTo';

function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // Blokkert lagring eller en testmiljø uten `window`: gjestemodus varer da bare til omlasting.
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    // Se `read`.
  }
}

let organizationId: string | null = typeof window === 'undefined' ? null : read(ORGANIZATION_KEY);
let returnTo: string | null = typeof window === 'undefined' ? null : read(RETURN_KEY);
let scope: { organizationId: string; scope: GuestScope } | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Organisasjonen fanen står i som gjest, eller null. */
export function getGuestOrganizationId(): string | null {
  return organizationId;
}

/** Hvor «Tilbake» i gjestelinja går — siden man kom fra. Null: Innboks. */
export function getGuestReturnTo(): string | null {
  return returnTo;
}

/**
 * Går i gjestemodus i `id`. `from` er stien man kom fra (kalender, oppgaver, tråden). Delingene fra
 * en annen organisasjon glemmes med en gang, så `can()` aldri leser feil organisasjons delinger.
 */
export function enterGuestOrganization(id: string, from: string | null): void {
  if (scope !== null && scope.organizationId !== id) scope = null;
  organizationId = id;
  returnTo = from;
  write(ORGANIZATION_KEY, id);
  write(RETURN_KEY, from);
  notify();
}

/** Ut av gjestemodus: tilbake til personens egne organisasjoner. */
export function leaveGuestOrganization(): void {
  if (organizationId === null && returnTo === null && scope === null) return;
  organizationId = null;
  returnTo = null;
  scope = null;
  write(ORGANIZATION_KEY, null);
  write(RETURN_KEY, null);
  notify();
}

/** Delingene gjesten har i organisasjonen, slik `GET /v1/me/guest-scope` svarte. */
export function setGuestScope(id: string, next: GuestScope | null): void {
  scope = next === null ? null : { organizationId: id, scope: next };
}

/** Delingene i DENNE organisasjonen, eller null — aldri en annen organisasjons. */
export function guestScopeFor(id: string): GuestScope | null {
  return scope !== null && scope.organizationId === id ? scope.scope : null;
}

export function subscribeGuestOrganization(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useGuestOrganizationId(): string | null {
  return useSyncExternalStore(subscribeGuestOrganization, getGuestOrganizationId, () => null);
}
